/**
 * Fetch Twenty CRM SSO credentials from Control Plane,
 * then exchange for a loginToken via Twenty's SSO endpoint.
 *
 * Returns a loginToken that Twenty's /verify?loginToken=XXX route
 * can exchange for access + refresh tokens automatically.
 *
 * Mirrors fetchGhostSso.ts pattern for the CP call.
 * GOTCHA: CP JWT auth for allow_guest endpoints requires
 *   access_token in body, NOT Authorization header.
 */

import { getCPToken } from "@/lib/api/controlPlaneClient";
import type { TwentySsoResponse, TwentySsoLoginResponse } from "./types";

const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || "https://control.zaviago.com";

const TWENTY_CRM_FALLBACK_URL = "https://crm.zaviago.com";

export interface TwentyLoginResult {
  loginToken: string;
  /** Workspace-specific CRM URL (e.g. https://zaviagocom.crm.zaviago.com) */
  crmUrl: string;
}

/**
 * Step 1: Get SSO token from CP (includes workspace-specific crm_url)
 * Step 2: POST SSO token to Twenty's /auth/sso/zaviago → get loginToken
 * Returns loginToken + workspace CRM URL for iframe construction.
 */
export async function fetchTwentyLoginToken(
  tenantName: string,
  userEmail: string
): Promise<TwentyLoginResult> {
  const token = getCPToken();
  if (!token) {
    throw new Error("Not authenticated to Control Plane");
  }

  // Step 1: Get SSO token from CP
  const cpResponse = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.twenty.get_sso_url`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        access_token: token,
        tenant_name: tenantName,
        email: userEmail,
      }),
    }
  );

  if (!cpResponse.ok) {
    const error = await cpResponse.json().catch(() => ({}));
    let message = "Failed to get Twenty CRM SSO URL";
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        message =
          typeof msgs[0] === "string" ? msgs[0] : JSON.stringify(msgs[0]);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    }
    throw new Error(message);
  }

  const cpData = await cpResponse.json();
  const ssoData = cpData.message as TwentySsoResponse;

  const crmUrl = ssoData.crm_url || TWENTY_CRM_FALLBACK_URL;

  // Step 2: POST SSO token to Twenty's endpoint → get loginToken
  // In dev, proxy through Vite to bypass CORS (Twenty only allows work.zaviago.com).
  // In production, call Twenty directly (CORS allows the production origin).
  const ssoEndpoint = import.meta.env.DEV
    ? "/__twenty_sso/sso/zaviago"
    : `${TWENTY_CRM_FALLBACK_URL}/auth/sso/zaviago`;
  const twentyResponse = await fetch(ssoEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify({ token: ssoData.sso_token }),
  });

  if (!twentyResponse.ok) {
    const error = await twentyResponse.json().catch(() => ({}));
    throw new Error(error.message || "Twenty CRM SSO authentication failed");
  }

  const loginData = (await twentyResponse.json()) as TwentySsoLoginResponse;
  return { loginToken: loginData.loginToken, crmUrl };
}
